import React from 'react';
import './Explainability.css';

/**
 * DerivedRuleCard
 *
 * Renders a single decision rule with a "Derived" badge when the rule
 * was synthesized from rule-like nodes (override / scope / decision /
 * fact / rule kinds) rather than coming from a compiled gate. Always
 * shows the source section + page anchors when available.
 *
 * Props:
 *  - rule:           DecisionRule (from BusinessExplanation.decision_rules_evaluated)
 *  - onCiteClick:    optional handler when a [N] anchor is clicked
 */
const DerivedRuleCard = ({ rule, onCiteClick }) => {
  if (!rule) return null;
  const pages = (rule.source_pages || []).filter((p) => p);
  const sectionLabel = rule.source_section || rule.source_policy || '';

  const handleCiteClick = (e) => {
    e.preventDefault();
    if (onCiteClick && rule.cite_num) onCiteClick(rule.cite_num);
  };

  return (
    <div
      className={
        'explain-rule-card' +
        (rule.derived ? ' explain-rule-card--derived' : '') +
        (rule.satisfied ? ' explain-rule-card--satisfied' : ' explain-rule-card--unsatisfied')
      }
    >
      <div className="explain-rule-card-header">
        <span className="explain-rule-card-name">{rule.rule_name}</span>
        <div className="explain-rule-card-badges">
          {rule.rule_type && (
            <span className="explain-rule-card-type">{rule.rule_type}</span>
          )}
          {rule.derived && (
            <span className="explain-rule-card-derived-badge" title="Synthesized from rule-like source content because no compiled gate was associated with this answer.">
              Derived
            </span>
          )}
          {rule.cite_num != null && (
            <button
              type="button"
              className="explain-rule-card-cite"
              onClick={handleCiteClick}
              title={`Jump to citation [${rule.cite_num}] in the answer`}
            >
              [{rule.cite_num}]
            </button>
          )}
          <span className={`explain-rule-card-status explain-rule-card-status--${rule.satisfied ? 'yes' : 'no'}`}>
            {rule.satisfied ? 'Satisfied' : 'Not satisfied'}
          </span>
        </div>
      </div>

      {rule.rule_description && (
        <p className="explain-rule-card-description">{rule.rule_description}</p>
      )}

      {(sectionLabel || pages.length > 0) && (
        <div className="explain-rule-card-source">
          {sectionLabel && (
            <span className="explain-rule-card-section">{sectionLabel}</span>
          )}
          {pages.length > 0 && (
            <span className="explain-rule-card-pages">p.{pages.join(', ')}</span>
          )}
        </div>
      )}

      {rule.criteria && rule.criteria.length > 0 && (
        <ul className="explain-rule-card-criteria">
          {rule.criteria.map((c, i) => (
            <li
              key={i}
              className={
                'explain-rule-card-criterion' +
                (c.satisfied
                  ? ' explain-rule-card-criterion--yes'
                  : ' explain-rule-card-criterion--no')
              }
            >
              <span className="explain-rule-card-criterion-marker">{c.satisfied ? '✓' : '×'}</span>
              <span className="explain-rule-card-criterion-name">{c.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DerivedRuleCard;
